import { ChangeEvent, FunctionComponent, useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  createFilterOptions,
  Autocomplete,
  InputAdornment,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { WidgetProps } from '@rjsf/core'
import { GenericInput, Option } from '@dxp-module/common'
import myAccountGenericInputStyles from '../myAccountGenericInput.styles'
import autoCompleteStyles from './autocomplete.styles'

const AutocompleteInput: FunctionComponent<WidgetProps> = props => {
  const { label, required, id, value, options, onChange, placeholder } = props
  const styles = autoCompleteStyles()
  const genericInputStyles = myAccountGenericInputStyles()
  const [dropdownOpened, setDropdownOpen] = useState(false)
  const filterOptions = createFilterOptions<Option>({
    matchFrom: 'any',
    limit: 50,
  })

  function handleOnChange(option: Option | null) {
    if (option) {
      onChange(option.value)
    }
  }

  const classes = {
    root: clsx([genericInputStyles.root, styles.root]),
    fieldWrapper: genericInputStyles.fieldWrapper,
    input: genericInputStyles.input,
    errorText: genericInputStyles.errorText,
    notchedOutline: genericInputStyles.notchedOutline,
    focused: genericInputStyles.focused,
    inputError: genericInputStyles.inputError,
    helperTextError: genericInputStyles.helperTextError,
    dateCalendar: genericInputStyles.dateCalendar,
    label: genericInputStyles.label,
    phonePrefix: genericInputStyles.phonePrefix,
    noOptions: genericInputStyles.noOptions,
    inputNoLabel: genericInputStyles.inputNoLabel,
    labelContainer: genericInputStyles.labelContainer,
    labelAsterisk: genericInputStyles.labelAsterisk,
  }

  const defaultValue = useMemo(
    () =>
      Array.isArray(options?.enumOptions) &&
      options?.enumOptions?.find((option: Option) => option.value === value),
    []
  )

  return (
    <Autocomplete
      defaultValue={defaultValue}
      filterOptions={filterOptions}
      noOptionsText={String(options?.invalidOption || '')}
      onChange={(_: ChangeEvent<{ value?: string }>, option: Option | null) =>
        handleOnChange(option)
      }
      onOpen={() => setDropdownOpen(true)}
      onClose={() => setDropdownOpen(false)}
      classes={{
        input: styles.input,
        noOptions: styles.noOptions,
      }}
      id={id}
      options={Array.isArray(options?.enumOptions) ? options?.enumOptions : []}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      data-test={`autocomplete_input:${id}`}
      renderInput={params => {
        const inputProps = {
          label,
          required,
          id,
          value,
          params,
          placeholder,
          tooltip: options?.tooltip,
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon
                className={clsx([
                  styles.dropdownIcon,
                  {
                    [styles.dropdownIconOpened]: dropdownOpened,
                  },
                ])}
              />
            </InputAdornment>
          ),
        }
        if (options?.displayKeyOnSelect && !dropdownOpened) {
          return (
            <GenericInput classes={classes} {...inputProps} value={value} />
          )
        }

        return <GenericInput classes={classes} {...inputProps} />
      }}
    />
  )
}

export default AutocompleteInput
